/**
 * V108-6: the setup screens' reads — enablers (with observed progress) and coverage, the one count source.
 * While a step is waiting or failed both are re-read every 15 s (the "we check every 15 s" promise), so a
 * step that reaches Done moves every number on the page at once. V108-8-21: the server answers from a stored view;
 * while it computes a newer one (`computing`) both are re-read every 3 s.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listWorkspaceMembers } from '../../lib/api';
import { getCoverage, listEnablers } from '../../lib/setup-api';
import type { Person } from '../../lib/setup-model';

export const POLL_MS = 15_000;
/** V108-8-21: how soon a view the server is still computing is read again. */
export const COMPUTING_POLL_MS = 3_000;
/** After this long without a first view, the page stops fast polling and offers Retry. */
export const COMPUTING_GIVE_UP_MS = 60_000;
export const setupKeys = {
  enablers: (slug: string) => ['setup-enablers', slug] as const,
  coverage: (slug: string) => ['setup-coverage', slug] as const,
  noSource: (slug: string, program: string) => ['setup-no-source', slug, program] as const,
  cloudAccounts: (slug: string) => ['setup-cloud-accounts', slug] as const,
};

export function useSetupData(slug: string, opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled ?? true;
  // V108-8-21: when the current `computing` period began (null while nothing is being computed). After a minute the
  // page stops fast polling and offers Retry, for a first compute and an update alike; Retry starts a new minute.
  const [since, setSince] = useState<number | null>(null);
  const [expired, setExpired] = useState(false);
  const enablers = useQuery({
    queryKey: setupKeys.enablers(slug), queryFn: () => listEnablers(slug), enabled: enabled && !!slug, staleTime: 5_000,
    refetchInterval: (q) => (q.state.data?.computing ? (expired ? false : COMPUTING_POLL_MS)
      : (q.state.data?.enablers ?? []).some((e) => e.progress.state === 'waiting' || e.progress.state === 'failed') ? POLL_MS : false),
  });
  const inFlight = (enablers.data?.enablers ?? []).some((e) => e.progress.state === 'waiting' || e.progress.state === 'failed');
  const coverage = useQuery({
    queryKey: setupKeys.coverage(slug), queryFn: () => getCoverage(slug), enabled: enabled && !!slug, staleTime: 5_000,
    refetchInterval: (q) => ((q.state.data?.computing || enablers.data?.computing) && !expired ? COMPUTING_POLL_MS : inFlight ? POLL_MS : false),
  });
  // One clock for the page: computing while either view is, as old as the older of the two.
  const computing = !!(enablers.data?.computing || coverage.data?.computing);
  const computedAt = [enablers.data?.computed_at, coverage.data?.computed_at].filter((x): x is string => !!x).sort()[0] ?? null;
  useEffect(() => {
    if (computing && since === null) setSince(Date.now());
    if (!computing && since !== null) { setSince(null); setExpired(false); }
  }, [computing, since]);
  useEffect(() => {
    if (since === null) return;
    const t = window.setTimeout(() => setExpired(true), Math.max(0, since + COMPUTING_GIVE_UP_MS - Date.now()));
    return () => window.clearTimeout(t);
  }, [since]);
  const freshness: SetupFreshnessState = { computing, computedAt, stuck: computing && expired };
  /** Read both again and give the compute another minute (the "taking longer" Retry). */
  const retry = () => { setExpired(false); setSince(Date.now()); void enablers.refetch(); void coverage.refetch(); };
  return { enablers, coverage, freshness, retry };
}

/** V108-8-21: how fresh the stored setup view is (one clock for steps and coverage). */
export interface SetupFreshnessState { computing: boolean; computedAt: string | null; stuck: boolean }

/** After a setup write: re-read the steps, coverage and no-source lists together (one count source). */
export function useRefreshSetup(slug: string) {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: setupKeys.enablers(slug) }),
    qc.invalidateQueries({ queryKey: setupKeys.coverage(slug) }),
    qc.invalidateQueries({ queryKey: ['setup-no-source', slug] }),
    qc.invalidateQueries({ queryKey: setupKeys.cloudAccounts(slug) }),
  ]);
}

export function usePeople(slug: string): Person[] {
  const q = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000, enabled: !!slug });
  return useMemo(() => (q.data?.members ?? []).map((m) => ({ user_id: m.user_id, name: m.display_name || m.email, role: m.role })), [q.data]);
}
